import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { AutocompleteInput, MobileNumberInput } from "_components";
import styles from "./index.module.css";
import {
  Box,
  Modal,
  Button,
  Link,
  Typography,
  Switch,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  styled,
} from "@mui/material";
import { logo } from "images";
import Grid from "@mui/material/Grid2";
import { labels } from "_utils/labels";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentBilling,
  getProfileInfo,
} from "selectors/energyAssistance.selectors";
import {
  getAgencyListApi,
  getAvailableAppointmentsApi,
  postReconnectServiceApi,
} from "services/energyAssistance/energyAssistance.thunks";
import { alertActions } from "_store";
import dayjs from "dayjs";
import { ERR_MSG } from "constants/labels";

const CustomSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 62, 
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(36px)", 
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#0059B3",
        opacity: 1,
        border: 0,
        ...theme.applyStyles("dark", {
          backgroundColor: "#0059B3",
        }),
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#0059B3",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color: theme.palette.grey[100],
      ...theme.applyStyles("dark", {
        color: theme.palette.grey[600],
      }),
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.7,
      ...theme.applyStyles("dark", {
        opacity: 0.3,
      }),
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    ...theme.applyStyles("dark", {
      backgroundColor: "#39393D",
    }),
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 11,
      fontWeight: 600,
    },
    "&::before": {
      content: '"Yes"',
      left: 6,
      color: "#fff",
    },
    "&::after": {
      content: '"No"',
      right: 6,
      color: "#666",
    },
  },
}));

const ReconnectModal = ({ open, setOpen }) => {
  const header = "Reconnect Account";
  const authUser = useSelector((x) => x.auth?.value);
  const currentBilling = useSelector(getCurrentBilling);
  const amountDue = currentBilling?.Data?.amountDue;
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const isStandardUser = userAccess?.some((access) =>
    access.Role.toLowerCase().includes("standard user"),
  );
  const profileInfo = useSelector(getProfileInfo);
  const dispatch = useDispatch();
  const { Data: profileInfoData } = profileInfo;
  const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState(
    profileInfoData?.HomePhone?.replace(
      /(\d{3})(\d{3})(\d{4})/,
      (_, p1, p2, p3) => [p1, p2, p3].filter(Boolean).join("-"),
    ) || "",
  );
  const [scheduleAppointment, setScheduleAppointment] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointmentValues, setAppointmentValues] = useState([]);
  const [agency, setAgency] = useState([]);

  const fetchAvailableAppointments = async () => {
    try {
      dispatch(alertActions.clear());
      const payload = {
        AccountNumber: profileInfoData?.AccountNumber,
      };
      const res = await dispatch(getAvailableAppointmentsApi(payload)).unwrap();
      if (!res || res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message || ERR_MSG["073"],
            header: header,
          }),
        );
        if (!isStandardUser) {
          handleClose();
        }
        return;
      }
      const appointments = res?.Data?.map((data) => {
        return { label: data, value: data };
      });
      setAppointmentValues(appointments);
    } catch (error) {
      handleClose();
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header,
        }),
      );
    }
  };

  useEffect(() => {
    fetchAvailableAppointments();
    fetchAgencyList();
  }, []);

  useEffect(() => {
    if (!isStandardUser) {
      setScheduleAppointment(true);
    }
  }, [isStandardUser]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors: formErrors, isValid, touchedFields },
    trigger,
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      phoneNumber: updatedPhoneNumber,
      isSeniorCitizen: false,
      dateOfBirth: null,
      pledgeAmount: "",
      AvailableSlot: null,
      appointmentAgreement: false,
    },
  });

  const isSeniorCitizen = watch("isSeniorCitizen");
  const availableSlot = watch("AvailableSlot");
  const appointmentAgreement = watch("appointmentAgreement");

  const fetchAgencyList = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(
        getAgencyListApi(profileInfoData?.AccountNumber)
      ).unwrap();
      const agencyList = res?.Data?.map((data) => {
        return { label: data?.AgencyName, value: data?.AgencyId };
      });
      setAgency(agencyList);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Pledge Amount",
        }),
      );
    }
  };

  const handleBlur = async (e) => {
    await trigger(e.target.name);
  };

  const phoneNumberField = ({ field }) => (
    <TextField
      {...field}
      placeholder="Enter phone number"
      variant="outlined"
      fullWidth
      size="small"
      error={!!formErrors.phoneNumber}
      helperText={formErrors.phoneNumber?.message}
    />
  );

  const seniorCitizenField = ({ field }) => (
    <Typography
      component={"div"}
      sx={{
        fontSize: "10px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >     
      <CustomSwitch
        {...field}
        checked={field.value}
        onChange={(e) => field.onChange(e.target.checked)}
      />
    </Typography>
  );

  const dateOfBirthField = ({ field }) => (
    <DatePicker
      {...field}
      views={["year", "month", "day"]}
      label="Date of Birth"
      value={field.value || null}
      disabled={!isSeniorCitizen}
      onChange={(newValue) => {
        field.onChange(newValue);
        trigger("dateOfBirth");
      }}
      slotProps={{
        textField: {
          size: "small",
          variant: "outlined",
          fullWidth: true,
          onBlur: (e) => {
            field.onBlur(e);
            handleBlur(e);
          },
          error: isSeniorCitizen && !!formErrors.dateOfBirth,
          helperText: isSeniorCitizen && formErrors.dateOfBirth?.message,
        },
      }}
    />
  );

  const pledgeAmountField = ({ field }) => (
    <TextField
      {...field}
      placeholder="Amount"
      variant="outlined"
      fullWidth
      size="small"
      type="number"
      error={!!formErrors.pledgeAmount}
      helperText={formErrors.pledgeAmount?.message}
      onBlur={handleBlur}
    />
  );

  const availableSlotsField = ({ field }) => (
    <Autocomplete
      value={field.value}
      fullWidth
      options={appointmentValues}
      disabled={isStandardUser && !scheduleAppointment}
      getOptionLabel={(option) => option?.label || ""}
      onChange={(_, value) => {
        field.onChange(value);
      }}
      onClose={() => {
        field.onBlur();
        trigger("AvailableSlot");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label="Available Slots"
          variant="outlined"
          size="small"
          name={field.name}
          inputRef={field.ref}
          error={scheduleAppointment && !!formErrors.AvailableSlot}
          helperText={scheduleAppointment && formErrors.AvailableSlot?.message}
        />
      )}
    />
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  const handleAppointmentSwitchChange = (e) => {
    const value = e.target.checked;
    setScheduleAppointment(value);

    if (!value) {
      setSelectedDate(null);
      setValue("AvailableSlot", null);
      setValue("appointmentAgreement", false);
    }
  };

  const onSubmit = async (data) => {
    try {
      dispatch(alertActions.clear());
      const payload = {
        AccountNumber: profileInfoData?.AccountNumber,
        AgencyId: agency.length > 1 ? data.AgencyId : agency?.[0]?.value,
        phoneNumber: data.phoneNumber,
        seniorCitizen: data.isSeniorCitizen,
        seniorCitizenBirthDate: data.dateOfBirth
          ? dayjs(data.dateOfBirth).format("YYYY-MM-DD")
          : null,
        pledgeAmount: data.pledgeAmount,
        appointmentDate: data.AvailableSlot?.value,
      };

      const res = await dispatch(postReconnectServiceApi(payload)).unwrap();
      if (!res || res?.Succeeded === false) {
        dispatch(
          alertActions.error({
            message: res?.Message || ERR_MSG["073"],
            header: header,
          }),
        );
        handleClose();
        return;
      } else {
        dispatch(
          alertActions.success({
            message: res?.Message,
            header: header,
          }),
        );
        setUpdatedPhoneNumber(data.phoneNumber);
        handleClose();
      }
    } catch (error) {
      handleClose();
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        }),
      );
    }
  };

  const shouldShowCheckboxes =
    !isStandardUser || (isStandardUser && scheduleAppointment);

  const isSubmitDisabled = () => {
    const baseDisabled = !isValid || (scheduleAppointment && !availableSlot);
    if (shouldShowCheckboxes && availableSlot) {
      return baseDisabled || !appointmentAgreement;
    }
    return baseDisabled;
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
        className={styles.modalContainer}
      >
        <Box className={styles.modalBox}>
          <Box className={styles.modalBoxInner}>
            <Grid item xs={12}>
              <Link href="#" variant="logo" className="wgllogo">
                <img src={logo} alt="logo"></img>
                {labels.eServicePortal}
              </Link>
              <h5 className="headercontent">
                <b>{header}</b>
              </h5>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                  container
                  spacing={2}
                  className={styles.gridContainer}
                  p={1}
                >
                  <Typography className={styles.customerInformation}>
                    CUSTOMER INFORMATION
                  </Typography>
                  <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                    <Typography component="div">
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFields}>
                            Account Number
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFieldsDetails}>
                            {profileInfoData?.AccountNumber}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Typography>
                    <Typography component="div">
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFields}>
                            Customer Name
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFieldsDetails}>
                            {profileInfoData?.CustomerName}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Typography>
                    <Typography component="div">
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFields}>
                            Service Address
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFieldsDetails}>
                            {profileInfoData?.ServiceAddress}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Typography>
                    <Typography component="div">
                      <Grid container spacing={3}>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <Typography className={styles.infoFields}>
                            Phone Number
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                          <MobileNumberInput
                            control={control}
                            name="phoneNumber"
                            label="Phone Number"
                            rules={{
                              required: "Phone number is required",
                              pattern: {
                                value: /^\d{3}-\d{3}-\d{4}$/,
                                message: "Invalid phone number",
                              },
                            }}
                            errors={formErrors}
                            handleBlur={handleBlur}
                          />
                        </Grid>
                      </Grid>
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={1} pt={2}>
                  {agency?.length > 1 && (
                    <Grid
                      container
                      size={{ xs: 12, sm: 12, md: 12 }}
                      columnSpacing={1}
                    >
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <Box
                          display={"flex"}
                          height={"100%"}
                          alignItems={"center"}
                          component="div"
                          className="Username"
                        >
                          <Typography className={styles.formFieldLabel}>
                            Agency
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                        <Box display={"flex"}>
                          <AutocompleteInput
                            control={control}
                            name="AgencyId"
                            label="Select Agency"
                            options={agency}
                            forModalPopup={true}
                            size="small"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                  <Grid
                    container
                    size={{ xs: 12, sm: 12, md: 12 }}
                    columnSpacing={1}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <Box
                        display={"flex"}
                        justifyContent={"space-between"}
                        component="div"
                      >
                        <Typography className={styles.formFieldLabel}>
                          Is the customer a senior citizen?
                        </Typography>
                        <Controller
                          name="isSeniorCitizen"
                          control={control}
                          render={seniorCitizenField}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <Box display={"flex"}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Controller
                            name="dateOfBirth"
                            control={control}
                            rules={{
                              required: isSeniorCitizen
                                ? "Date of birth is required"
                                : false,
                            }}
                            render={dateOfBirthField}
                          />
                        </LocalizationProvider>
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    size={{ xs: 12, sm: 12, md: 12 }}
                    columnSpacing={1}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <Box
                        display={"flex"}
                        flexDirection={"column"}
                        component="div"
                        className="Username"
                      >
                        <Typography className={styles.formFieldLabel}>
                          Pledge Amount
                        </Typography>
                        <Typography fontWeight={"600"} fontSize={"10px"}>
                          {
                            "(Minimum pledge amount should be 75% of the balance amount.)"
                          }
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                      <Box display={"flex"}>
                        <Controller
                          name="pledgeAmount"
                          control={control}
                          rules={{
                            required: "Pledge amount is required",
                            min: {
                              value: 1,
                              message: "Amount must be greater than 0",
                            },
                          }}
                          render={pledgeAmountField}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {(!isStandardUser ||
                    (isStandardUser &&
                      appointmentValues.length !== 0 &&
                      watch("pledgeAmount") &&
                      parseFloat(watch("pledgeAmount")) >=
                        amountDue * 0.75)) && (
                    <>
                      <Grid container size={{ xs: 12, sm: 12, md: 12 }} pt={2}>
                        <Box
                          display={"flex"}
                          component="div"
                          justifyContent={"space-between"}
                          alignItems={"center"}
                          width={"100%"}
                        >
                          <Typography component={"div"} fontWeight={"700"}>
                            Schedule Appointment with Technician{" "}
                            {isStandardUser && "(Optional)"}
                          </Typography>
                          {isStandardUser && (
                            <Typography component={"div"}>
                              <CustomSwitch
                                checked={scheduleAppointment}
                                onChange={handleAppointmentSwitchChange}
                              />
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid container size={{ xs: 12, sm: 12, md: 12 }} mt={2}>
                        <Grid
                          container
                          size={{ xs: 12, sm: 12, md: 12 }}
                          spacing={2}
                        >
                          <Controller
                            name="AvailableSlot"
                            control={control}
                            rules={{
                              required: scheduleAppointment
                                ? "Please select time slot"
                                : false,
                            }}
                            render={availableSlotsField}
                          />
                        </Grid>
                      </Grid>
                      {shouldShowCheckboxes && (
                        <Grid
                          container
                          size={{ xs: 12, sm: 12, md: 12 }}
                          mt={2}
                        >
                          <Box width="100%">
                            <Controller
                              name="appointmentAgreement"
                              control={control}
                              render={({ field }) => (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      {...field}
                                      checked={field.value}
                                      disabled={!availableSlot}
                                      onChange={(e) =>
                                        field.onChange(e.target.checked)
                                      }
                                      sx={{ alignSelf: "flex-start", mt: -1 }}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography component="div">
                                        I have advised customer that:
                                      </Typography>
                                      <ul
                                        style={{
                                          marginTop: "8px",
                                          marginBottom: 0,
                                          paddingLeft: "20px",
                                        }}
                                      >
                                        <li>The appointment is for all-day.</li>
                                        <li>
                                          Someone 18 years or older will need to
                                          be available at the service location
                                          to relight gas appliances.
                                          <ul style={{ marginTop: "4px" }}>
                                            <li>
                                              If their gas appliance has an
                                              electronic ignition, electric
                                              service must be active before
                                              Washington Gas can turn on gas
                                              service to their appliances.
                                            </li>
                                            <li>
                                              If they have a gas water heater,
                                              their water service must be active
                                              before Washington Gas can relight
                                              the appliance.
                                            </li>
                                          </ul>
                                        </li>
                                      </ul>
                                    </Box>
                                  }
                                  sx={{ alignItems: "flex-start" }}
                                />
                              )}
                            />
                          </Box>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={styles.submitButton}
                  disabled={isSubmitDisabled()}
                >
                  Reconnect Account
                </Button>
              </form>

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                className={styles.closeButton}
                onClick={handleClose}
              >
                Cancel
              </Button>
            </Grid>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ReconnectModal;
